import { getDocumentYear } from "./document-year.utils"
import { Document } from "@/entities/document/model/document.types"
import env from "@/env"
import { getImageUrl } from "@/utils/get-image-url"
import { Establishment } from "@/entities/establishment/model/establishment.types"
import { getLegalIdentifier, getResidualRiskLevels, getRiskAttributeNames, AccountRisksSettings } from "@/redux/account.types"
import { toNamedRiskAttributes } from "@/entities/physical-risk/model/physical-risk.utils"
import { Session } from "@/redux/_session.types"
import { toast } from "react-toastify"
import { Site } from "@/entities/site/model/site.types"
import { Population } from "@/entities/population/model/population.types"
import { AppDispatch } from "@/core/root-reducer"

interface Fallbacks {
  establishment: Establishment
  accountRisksSettings: AccountRisksSettings
  populations: Population[]
  session: Session
  sites: Site[]
}

function getDocumentContent(document: Document) {
  return document.content ?? {}
}

function getRiskUnitName(risk: any, content: any, establishmentName: string) {
  const siteNames = (content.sites ?? []).filter((site) => risk.siteIds?.includes(site.id)).map((site) => site.name)

  const populationNames = (content.populations ?? [])
    .filter((population) => risk.populationIds?.includes(population.id))
    .map((population) => population.name)

  return [...siteNames, ...populationNames].join(", ") || establishmentName
}

//Le generateur (service qr-code) imprime les attributs par libelle : la conversion se fait ici,
//au moment de l'envoi, le stockage restant indexe par id d'attribut.
function mapPhysicalRiskToPdfRisk(risk: any, content: any, establishmentName: string, riskAttributes: any) {
  const family = risk.cFamily ?? content.riskFamilies?.find((item) => item.id === risk.RiskFamilyId)
  const actions = (content.preventionActions ?? []).filter((action) => action.PhysicalRiskId === risk.id)

  return {
    ...risk,
    attributes: toNamedRiskAttributes(risk, riskAttributes),
    actions: actions.map((action) => ({
      id: action.id,
      name: action.name,
      description: action.description,
      dateEnd: action.dateDone,
      dateEndPlanned: action.datePlanned,
      dateStarted: action.dateStarted,
      status: action.cStatus ?? (action.dateDone ? "done" : action.dateStarted ? "pending" : "todo"),
      budget: action.cost ?? 0,
      budgetSpend: 0,
      UserResponsibleId: action.UserId
    })),
    name: risk.cName || family?.name || Object.values(risk.attributes ?? {})[0] || "",
    nameSecondary: family?.name ?? "",
    frequencyLabel: risk.cFrequencyLabel,
    preventionLabel: risk.cPreventionLabel,
    severityLabel: risk.cSeverityLabel,
    unitName: getRiskUnitName(risk, content, establishmentName),
    unitSize: risk.cSize,
    valueEffective: risk.cValueEffective,
    valueEffectiveColor: risk.cValueEffectiveColor,
    valueEffectiveIndex: risk.cValueEffectiveIndex,
    valueEffectiveLabel: risk.cValueEffectiveLabel,
    valueInitial: risk.cValueInitial,
    valueInitialColor: risk.cValueInitialColor,
    valueInitialLabel: risk.cValueInitialLabel
  }
}

function getUnits(content: any, fallbacks: Fallbacks) {
  const sites = content.sites ?? fallbacks.sites
  const populations = content.populations ?? fallbacks.populations

  return [
    ...sites.map((item) => ({ ...item, type: "site" })),
    ...populations.map((item) => ({
      ...item,
      type: "population",
      siteNames: sites.filter((site) => site.populationIds?.includes(item.id)).map((site) => site.name)
    }))
  ].map((item) => ({
    id: item.id,
    type: item.type,
    name: item.name,
    description: item.description ?? "",
    size: item.size ?? null,
    siteNames: item.siteNames ?? []
  }))
}

function downloadPdfFromBuffer(buffer: any, filename: string) {
  const data = buffer?.data ? new Uint8Array(buffer.data) : new Uint8Array(buffer)
  const blob = new Blob([data], { type: "application/pdf" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")

  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export async function exportPapripactToPdf(dispatch: AppDispatch, document: Document): Promise<void> {
  const response: any = await dispatch({
    type: "QRCODE",
    payload: {
      method: "POST",
      url: "/generate/papripact",
      data: {
        programYear: getDocumentYear(document),
        generatedAt: document.content?.generatedAt ?? document.date,
        establishmentName: document.content?.establishment?.name ?? ""
      }
    }
  } as any)
  if (!response?.buffer || !response?.filename) throw new Error("PAPRIPACT PDF generation failed")
  downloadPdfFromBuffer(response.buffer, response.filename)
}

export async function exportDocumentToPdf(
  dispatch: AppDispatch,
  document: Document,
  fallbacks: Fallbacks
): Promise<void> {
  if (document.type === "papripact") {
    await exportPapripactToPdf(dispatch, document)
    return
  }

  const content = getDocumentContent(document)
  const establishment = content.establishment ?? fallbacks.establishment
  const accountRisksSettings = content.accountRisksSettings ?? content.organization ?? fallbacks.accountRisksSettings
  //Les baremes imprimes ne viennent que du document : ceux du compte ont pu changer depuis, et le
  //generateur n'imprime pas de bloc plutot que d'imprimer un barème faux.
  const snapshotSettings = content.accountRisksSettings ?? content.organization ?? null
  const accountName = content.accountName ?? accountRisksSettings.name ?? fallbacks.session.accountName
  const accountUsers = (content.users ?? []).filter((user) => {
    // Même périmètre que les administrateurs de l'organisation et les contributeurs de l'établissement.
    if (user.level > 0 || (user.AccountId && user.AccountId !== establishment.AccountId)) return false
    return (
      (user.role === "ADMIN" && !user.EstablishmentId) ||
      (Boolean(establishment.id) && user.EstablishmentId === establishment.id)
    )
  })
  const riskFamilies = content.riskFamilies ?? []
  const risks =
    content.risks ??
    (content.physicalRisks ?? []).map((risk) =>
      mapPhysicalRiskToPdfRisk(risk, content, establishment.name, accountRisksSettings.riskAttributes)
    )
  const units = getUnits(content, fallbacks)

  const response: any = await dispatch({
    type: "QRCODE",
    payload: {
      method: "POST",
      url: "/generate/duerp/v2",
      data: {
        risks,
        establishment: {
          id: establishment.id,
          name: establishment.name,
          address: establishment.address,
          siret: establishment.codeEstablishment,
          naf: establishment.industryCode
        },
        organization: {
          name: accountName,
          siren: getLegalIdentifier(accountRisksSettings),
          riskAttributes: getRiskAttributeNames(accountRisksSettings.riskAttributes),
          severityScale: snapshotSettings?.severityScale,
          frequencyScale: snapshotSettings?.frequencyScale,
          preventionScale: snapshotSettings?.preventionScale,
          residualRiskScale: accountRisksSettings.residualRiskScale,
          residualRiskLevels: getResidualRiskLevels(accountRisksSettings),
          users: accountUsers.map((user) => ({ name: user.username, role: user.role }))
        },
        riskFamilies: riskFamilies.map((family) => ({
          id: family.id,
          code: family.code,
          name: family.name,
          imageUrl: family.code ? `${env.REACT_APP_URL_SPACE}/RiskFamilies/${family.code}.png` : null
        })),
        units,
        documentDate: document.date ?? null,
        branding: {
          primaryColor: fallbacks.session.accountColors.brandPrimary,
          secondaryColor: fallbacks.session.accountColors.brandSecondary,
          logoUrl: fallbacks.session.accountOptions.image
            ? getImageUrl("Accounts", fallbacks.session.accountId, "")
            : null
        }
      }
    }
  } as any)

  if (response?.buffer && response?.filename) {
    downloadPdfFromBuffer(response.buffer, response.filename)
    return
  }

  const file = response?.file ?? (typeof response === "string" ? response : null)

  if (file) {
    window.open(`${env.REACT_APP_URL_QRCODE}/public/${file}`, "_blank")
  } else {
    toast("Le PDF n'a pas pu être généré.", { type: "error" })
  }
}
